import { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

interface FAQItem {
  question: string;
  answer: string;
}

interface SEOFaqSectionProps {
  faq?: FAQItem[];
  title?: string;
  className?: string;
}

export function SEOFaqSection({ faq, title = 'Domande Frequenti', className }: SEOFaqSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!faq || faq.length === 0) return null;

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={cn('bg-white rounded-xl border border-gray-200 overflow-hidden', className)}>
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <HelpCircle className="w-5 h-5 text-primary-600" />
        <h2 className="font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="divide-y divide-gray-100">
        {faq.map((item, index) => (
          <div key={index}>
            <button
              onClick={() => toggle(index)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium text-gray-900 pr-4">{item.question}</span>
              {openIndex === index ? (
                <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
              )}
            </button>
            {openIndex === index && (
              <div className="px-4 pb-4 text-gray-600">
                {item.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface DefinitionItem {
  term: string;
  definition: string;
  source?: string;
}

interface SEODefinitionsProps {
  definitions?: DefinitionItem[];
  title?: string;
}

export function SEODefinitions({ definitions, title = 'Glossario' }: SEODefinitionsProps) {
  if (!definitions || definitions.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900 mb-4">{title}</h2>
      <div className="space-y-4">
        {definitions.map((item, index) => (
          <div key={index}>
            <dt className="font-medium text-gray-900">{item.term}</dt>
            <dd className="text-gray-600 text-sm mt-1">{item.definition}</dd>
            {item.source && (
              <dd className="text-xs text-gray-400 mt-1">Fonte: {item.source}</dd>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface StatItem {
  value: string;
  label: string;
  subtitle?: string;
  note?: string;
}

interface SEOStatsProps {
  stats?: StatItem[];
  title?: string;
}

export function SEOStats({ stats, title }: SEOStatsProps) {
  if (!stats || stats.length === 0) return null;

  return (
    <div className="bg-slate-900 rounded-xl p-6">
      {title && <h2 className="font-semibold text-white mb-4">{title}</h2>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="text-center">
            <div className="text-2xl font-bold text-orange-400">{stat.value}</div>
            <div className="text-sm text-gray-300">{stat.label}</div>
            {stat.subtitle && (
              <div className="text-xs text-gray-400">{stat.subtitle}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}